import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { QueryStatus, authAxios } from "../../../utils";
import { RootState } from "../../../redux/store";
import { GetChargesMonthsOfCompanyResponse } from "@qivia/schemas-ev-charging";
import {
  GetHomeChargingSupportingDocumentsPayload,
  HomeChargingSupportingDocument,
  HomeChargingSupportingDocumentsToCreate,
} from "./homeChargingSupportingDocumentsAPI";
import { fakeHomeChargingSupportingDocumentsByMonth } from "./homeChargingSupportingDocumentsFakeData";

export interface HomeChargingSupportingDocumentsState {
  homeChargingSupportingDocumentsStatus: QueryStatus;
  homeChargingSupportingDocuments: HomeChargingSupportingDocument[];
  homeChargingSupportingDocumentsByPeriod: Record<
    string,
    HomeChargingSupportingDocument[]
  >;
  homeChargingSupportingDocumentsPeriod:
    | GetHomeChargingSupportingDocumentsPayload
    | undefined;
  createHomeChargingSupportingDocumentsStatus: QueryStatus;
  createdHomeChargingSupportingDocumentsCount: number | null;
  homeChargingChargesMonthsStatus: QueryStatus;
  homeChargingChargesMonths: GetChargesMonthsOfCompanyResponse;
}

const initialState: HomeChargingSupportingDocumentsState = {
  homeChargingSupportingDocumentsStatus: "idle",
  homeChargingSupportingDocuments: [],
  homeChargingSupportingDocumentsByPeriod: {},
  homeChargingSupportingDocumentsPeriod: undefined,
  createHomeChargingSupportingDocumentsStatus: "idle",
  createdHomeChargingSupportingDocumentsCount: null,
  homeChargingChargesMonthsStatus: "idle",
  homeChargingChargesMonths: [],
};

export const getHomeChargingSupportingDocumentsAsync = createAsyncThunk(
  "getHomeChargingSupportingDocumentsAsync/call",
  (payload: GetHomeChargingSupportingDocumentsPayload, thunkAPI) => {
    const supportingDocuments =
      fakeHomeChargingSupportingDocumentsByMonth[
        new Date(payload.from).getMonth()
      ] ?? [];
    const supportingDocumentsList = supportingDocuments.map(
      (supportingDocument) => {
        const includedCharges = supportingDocument.charges.filter(
          (charge) => charge.excludedAt === null,
        );
        return {
          ...supportingDocument,
          chargesCount: includedCharges.length,
          totalEnergy: Number(
            includedCharges
              .reduce((sum, charge) => sum + charge.totalEnergy, 0)
              .toFixed(2),
          ),
          reimbursementAmount: Number(
            includedCharges
              .reduce((sum, charge) => sum + charge.reimbursementAmount, 0)
              .toFixed(2),
          ),
        };
      },
    );

    thunkAPI.dispatch(
      homeChargingSupportingDocumentsSlice.actions.setHomeChargingSupportingDocumentsByPeriod(
        {
          from: payload.from,
          supportingDocuments: supportingDocumentsList,
        },
      ),
    );
    return supportingDocumentsList;
  },
);

export const getHomeChargingChargesMonthsAsync = createAsyncThunk(
  "getHomeChargingChargesMonths/call",
  async (_, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    if (!company) throw new Error("Company not selected");
    const axios = authAxios({ companyUuid: company.uuid });
    const response = await axios.get<GetChargesMonthsOfCompanyResponse>(
      `electric_charges/months/company/${company.uuid}`,
    );
    return response.data;
  },
);

export const createHomeChargingSupportingDocumentsAsync = createAsyncThunk(
  "createHomeChargingSupportingDocuments/call",
  (supportingDocumentsToCreate: HomeChargingSupportingDocumentsToCreate) => {
    return supportingDocumentsToCreate;
  },
);

export const homeChargingSupportingDocumentsSlice = createSlice({
  name: "homeChargingSupportingDocuments",
  initialState,
  reducers: {
    setHomeChargingSupportingDocumentsPeriod(
      state,
      action: PayloadAction<
        GetHomeChargingSupportingDocumentsPayload | undefined
      >,
    ) {
      state.homeChargingSupportingDocumentsPeriod = action.payload;
      if (action.payload) {
        state.homeChargingSupportingDocuments =
          state.homeChargingSupportingDocumentsByPeriod[action.payload.from] ??
          state.homeChargingSupportingDocuments;
      }
    },
    resetHomeChargingSupportingDocumentsStatus(state) {
      state.homeChargingSupportingDocumentsStatus = "idle";
      state.homeChargingSupportingDocumentsByPeriod = {};
    },
    resetCreateHomeChargingSupportingDocumentsStatus(state) {
      state.createHomeChargingSupportingDocumentsStatus = "idle";
      state.createdHomeChargingSupportingDocumentsCount = null;
    },
    resetHomeChargingChargesMonthsStatus(state) {
      state.homeChargingChargesMonthsStatus = "idle";
    },
    setHomeChargingSupportingDocumentsByPeriod(
      state,
      action: PayloadAction<{
        from: string;
        supportingDocuments: HomeChargingSupportingDocument[];
      }>,
    ) {
      state.homeChargingSupportingDocumentsByPeriod[action.payload.from] =
        action.payload.supportingDocuments;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getHomeChargingSupportingDocumentsAsync.pending, (state) => {
        state.homeChargingSupportingDocumentsStatus = "processing";
      })
      .addCase(
        getHomeChargingSupportingDocumentsAsync.fulfilled,
        (state, action) => {
          state.homeChargingSupportingDocumentsStatus = "success";
          state.homeChargingSupportingDocuments = action.payload;
        },
      )
      .addCase(getHomeChargingSupportingDocumentsAsync.rejected, (state) => {
        state.homeChargingSupportingDocumentsStatus = "failed";
      })
      .addCase(createHomeChargingSupportingDocumentsAsync.pending, (state) => {
        state.createHomeChargingSupportingDocumentsStatus = "processing";
      })
      .addCase(
        createHomeChargingSupportingDocumentsAsync.fulfilled,
        (state, action) => {
          state.createHomeChargingSupportingDocumentsStatus = "success";
          state.createdHomeChargingSupportingDocumentsCount =
            action.payload.drivers.length;
        },
      )
      .addCase(createHomeChargingSupportingDocumentsAsync.rejected, (state) => {
        state.createHomeChargingSupportingDocumentsStatus = "failed";
      })
      .addCase(getHomeChargingChargesMonthsAsync.pending, (state) => {
        state.homeChargingChargesMonthsStatus = "processing";
      })
      .addCase(getHomeChargingChargesMonthsAsync.fulfilled, (state, action) => {
        state.homeChargingChargesMonthsStatus = "success";
        state.homeChargingChargesMonths = action.payload;
      })
      .addCase(getHomeChargingChargesMonthsAsync.rejected, (state) => {
        state.homeChargingChargesMonthsStatus = "failed";
      });
  },
});

export const selectHomeChargingSupportingDocumentsStatus = (state: RootState) =>
  state.homeChargingSupportingDocuments.homeChargingSupportingDocumentsStatus;

export const selectHomeChargingSupportingDocuments = (state: RootState) =>
  state.homeChargingSupportingDocuments.homeChargingSupportingDocuments;

export const selectHomeChargingSupportingDocumentsByPeriod = (
  state: RootState,
) =>
  state.homeChargingSupportingDocuments.homeChargingSupportingDocumentsByPeriod;

export const selectHomeChargingSupportingDocumentsPeriod = (state: RootState) =>
  state.homeChargingSupportingDocuments.homeChargingSupportingDocumentsPeriod;

export const selectCreateHomeChargingSupportingDocumentsStatus = (
  state: RootState,
) =>
  state.homeChargingSupportingDocuments
    .createHomeChargingSupportingDocumentsStatus;

export const selectCreatedHomeChargingSupportingDocumentsCount = (
  state: RootState,
) =>
  state.homeChargingSupportingDocuments
    .createdHomeChargingSupportingDocumentsCount;

export const selectHomeChargingChargesMonths = (state: RootState) =>
  state.homeChargingSupportingDocuments.homeChargingChargesMonths;

export const selectHomeChargingChargesMonthsStatus = (state: RootState) =>
  state.homeChargingSupportingDocuments.homeChargingChargesMonthsStatus;

export default homeChargingSupportingDocumentsSlice.reducer;
